import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Storage } from "aws-amplify";
import { toast } from "react-toastify";
import { isPngExtension } from "../../../helpers/functions";
import Modal from "../../../components/generic/modal";
import { RevAlert, RevBarra, RevButton, RevCard, RevChip, RevField, RevKvRow, RevSegmented, RevSwitch, RevTipRow, cx } from "../../../components/ds";
import {
    DEFAULT_OVERLAY, DEFAULT_SETTINGS, OPACITIES, SAMPLE, SIZES,
    hasCustomLogo, inspectImage, loadImage, metadataToSettings, minLogoWidth, placeholderPng, previewStyle, settingsToMetadata,
} from "../../../helpers/watermark";

/* Claves S3 (nivel public/ de Amplify). Watermark.png es el logo original con los ajustes en
   metadatos: es lo único que se sube y lo que compone el backend sobre cada miniatura. Los dos
   Sponsor* son los overlays de la antigua pestaña Patrocinador: si un evento los tiene y aún no
   tiene Watermark.png, el backend sigue pegándolos (modo heredado). */
const configKeys = (eventId) => ({
    source: `${eventId}/Configuration/Watermark.png`,
    landscape: `${eventId}/Configuration/SponsorLandscape.png`,
});

const sameSettings = (a, b) => a.position === b.position && a.size === b.size && a.opacity === b.opacity && a.enabled === b.enabled;

/** URL de objeto para un Blob, revocada al cambiar o desmontar. */
const useObjectUrl = (blob) => {
    const [url, setUrl] = useState(undefined);
    useEffect(() => {
        if (!blob) {
            setUrl(undefined);
            return undefined;
        }
        const next = URL.createObjectURL(blob);
        setUrl(next);
        return () => URL.revokeObjectURL(next);
    }, [blob]);
    return url;
};

const EventWatermark = ({ eventId }) => {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [savedSettings, setSavedSettings] = useState(DEFAULT_SETTINGS);
    const [logoImg, setLogoImg] = useState(null);          // logo en edición
    const [logoFile, setLogoFile] = useState(null);        // Blob/File del logo en edición
    const [savedLogo, setSavedLogo] = useState(null);      // { img, file } guardados en S3
    const [legacyUrl, setLegacyUrl] = useState(undefined); // overlay antiguo (pestaña Patrocinador) sin logo original
    const [orientation, setOrientation] = useState('landscape');
    const [warnings, setWarnings] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [barra, setBarra] = useState({});                // { ok } | { error }

    /* --- carga del estado guardado ------------------------------------------------ */
    useEffect(() => {
        let cancelled = false;
        const keys = configKeys(eventId);
        const fetchState = async () => {
            setLoading(true);
            try {
                const listed = await Storage.list(`${eventId}/Configuration/`, { pageSize: 99 });
                const present = new Set((listed.results || []).map((item) => item.key));
                const hasSource = present.has(keys.source);
                const hasLandscape = present.has(keys.landscape);

                if (hasSource) {
                    const obj = await Storage.get(keys.source, { download: true });
                    const saved = metadataToSettings(obj.Metadata);
                    if (!hasCustomLogo(obj.Metadata)) {
                        // Solo ajustes (logo=none): sin logo propio, con la marca de Revopic activa o apagada.
                        if (cancelled) return;
                        setSavedLogo(null);
                        setLogoImg(null);
                        setLogoFile(null);
                        setSettings(saved);
                        setSavedSettings(saved);
                        setLegacyUrl(undefined);
                    } else {
                        const img = await loadImage(obj.Body);
                        if (cancelled) return;
                        setSavedLogo({ img, file: obj.Body });
                        setLogoImg(img);
                        setLogoFile(null);
                        setSettings(saved);
                        setSavedSettings(saved);
                        setLegacyUrl(undefined);
                    }
                } else if (hasLandscape) {
                    const url = await Storage.get(keys.landscape);
                    if (cancelled) return;
                    setLegacyUrl(url);
                }
            } catch (err) {
                console.log(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchState();
        return () => { cancelled = true; };
    }, [eventId]);

    /* --- selección del logo -------------------------------------------------------- */
    const acceptFile = useCallback(async (file) => {
        if (!file) return;
        if (!isPngExtension(file.name) || (file.type && file.type !== 'image/png')) {
            setErrorMessage(t('eventWatermark.error.notPng'));
            return;
        }
        try {
            const img = await loadImage(file);
            const info = inspectImage(img);
            const warns = [];
            if (!info.hasAlpha) warns.push(t('eventWatermark.warn.noAlpha'));
            setWarnings(warns);
            setLogoImg(img);
            setLogoFile(file);
            setBarra({});
        } catch (err) {
            console.log(err);
            setErrorMessage(t('eventWatermark.error.notPng'));
        }
    }, [t]);

    const onInputChange = (e) => {
        acceptFile(e.target.files?.[0]);
        e.target.value = "";
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        acceptFile(e.dataTransfer?.files?.[0]);
    };

    const openPicker = () => fileInputRef.current?.click();

    /* --- guardar / cancelar -------------------------------------------------------- */
    /* Tres modos: logo propio (hasLogo), overlay de la antigua pestaña Patrocinador (isLegacy) y
       sin logo propio (isDefault): en este último la marca de Revopic se enseña y se puede apagar. */
    const hasLogo = logoImg !== null;
    const isLegacy = !hasLogo && legacyUrl !== undefined;
    const isDefault = !hasLogo && !isLegacy;
    const dirty = logoFile !== null
        || (hasLogo && !sameSettings(settings, savedSettings))
        || (isDefault && settings.enabled !== savedSettings.enabled);
    const canSave = !loading && !saving && dirty && (hasLogo || isDefault);

    const dispatchCancel = () => {
        setSettings(savedSettings);
        setLogoImg(savedLogo ? savedLogo.img : null);
        setLogoFile(null);
        setWarnings([]);
        setBarra({});
    };

    const dispatchSubmit = async () => {
        if (!canSave) return;
        setSaving(true);
        setBarra({});
        const keys = configKeys(eventId);
        try {
            // Un solo PUT: el logo original con los ajustes en metadatos. Cuando solo cambian los
            // ajustes se vuelve a subir el mismo Blob (S3 no cambia metadatos sin reescribir) y ese
            // PUT es lo que dispara el re-sellado de las miniaturas en el backend.
            // Sin logo propio se sube un PNG de 1×1 con logo=none: solo lleva los ajustes (la marca de
            // Revopic activa o apagada) y el backend no lo usa como logo.
            const sourceBlob = logoFile || savedLogo?.file || placeholderPng();
            await Storage.put(keys.source, sourceBlob, { contentType: 'image/png', metadata: settingsToMetadata(settings, { customLogo: hasLogo }) });
            setSavedLogo(hasLogo ? { img: logoImg, file: sourceBlob } : null);
            setSavedSettings(settings);
            setLogoFile(null);
            setLegacyUrl(undefined);
            setBarra({ ok: t('eventWatermark.saved') });
        } catch (err) {
            console.log(err);
            setBarra({ error: t('eventWatermark.saveError') });
            toast.error(t('eventWatermark.saveError'));
        } finally {
            setSaving(false);
        }
    };

    /* --- derivados de presentación ------------------------------------------------- */
    const posItems = [
        { value: "tl", label: t('eventWatermark.placement.pos.tl') },
        { value: "tr", label: t('eventWatermark.placement.pos.tr') },
        { value: "bl", label: t('eventWatermark.placement.pos.bl') },
        { value: "br", label: t('eventWatermark.placement.pos.br') },
        { value: "center", label: t('eventWatermark.placement.pos.center') },
    ];
    const sizeItems = Object.keys(SIZES).map((value) => ({ value, label: t(`eventWatermark.placement.size.${value}`), className: "rev-num" }));
    const opacityItems = Object.keys(OPACITIES).map((value) => ({ value, label: t(`eventWatermark.placement.opacity.${value}`), className: "rev-num" }));
    const orientationItems = [
        { value: "landscape", label: t('eventWatermark.preview.orientation.landscape') },
        { value: "portrait", label: t('eventWatermark.preview.orientation.portrait') },
    ];

    const chip = loading
        ? { label: '—' }
        : hasLogo
            ? (settings.enabled ? { label: t('eventWatermark.state.active'), tone: 'ok' } : { label: t('eventWatermark.state.off'), tone: 'warn' })
            : isLegacy
                ? { label: t('eventWatermark.state.legacy'), tone: 'info' }
                : settings.enabled
                    ? { label: t('eventWatermark.state.default'), tone: 'info' }
                    : { label: t('eventWatermark.state.off'), tone: 'warn' };

    /* El aviso de logo pequeño depende del tamaño elegido: cambia al moverlo, no solo al elegir fichero. */
    const logoWidth = hasLogo ? (logoImg.naturalWidth || logoImg.width) : 0;
    const smallLogo = hasLogo && logoWidth < minLogoWidth(settings.size)
        ? t('eventWatermark.warn.small', { width: logoWidth, size: SIZES[settings.size], min: minLogoWidth(settings.size) })
        : null;

    const posLabel = posItems.find((i) => i.value === settings.position)?.label;
    const summary = hasLogo
        ? (settings.enabled
            ? t('eventWatermark.preview.summary', { pos: posLabel, size: SIZES[settings.size], opacity: OPACITIES[settings.opacity] })
            : t('eventWatermark.preview.summaryOff'))
        : isDefault && !settings.enabled
            ? t('eventWatermark.preview.summaryOff')
            : t('eventWatermark.preview.summaryEmpty');

    const galleryValue = !isLegacy && !settings.enabled
        ? { v: t('eventWatermark.rules.gallery.off'), tone: 'no' }
        : { v: t('eventWatermark.rules.gallery.v') };

    /* Vista previa: la foto de muestra de la orientación elegida (el overlay heredado es 3:2, así
       que en ese modo la previa es siempre horizontal) y el logo colocado con la geometría del backend. */
    const sample = SAMPLE[isLegacy ? 'landscape' : orientation];
    const logoUrl = useObjectUrl(logoFile || (hasLogo && savedLogo ? savedLogo.file : null));
    const logoStyle = hasLogo
        ? previewStyle({ width: logoImg.naturalWidth || logoImg.width, height: logoImg.naturalHeight || logoImg.height }, orientation, settings)
        : undefined;

    const update = (patch) => {
        setSettings((s) => ({ ...s, ...patch }));
        setBarra({});
    };

    return (
        <>
            <div className="rev-cols rev-cols--flush">
                <div className="rev-cols__main">
                    <RevCard
                        title={t('eventWatermark.preview.title')}
                        sub={t('eventWatermark.preview.intro')}
                        head={<RevChip tone={chip.tone}>{chip.label}</RevChip>}>
                        {isLegacy &&
                            <RevAlert tone="info" title={t('eventWatermark.legacy.title')}>
                                {t('eventWatermark.legacy.body')}
                            </RevAlert>}
                        {warnings.map((w) => <RevAlert key={w} tone="warn">{w}</RevAlert>)}
                        {smallLogo && <RevAlert tone="warn">{smallLogo}</RevAlert>}
                        <div className="rev-wm-stage">
                            <div className={cx('rev-wm-photo', `rev-wm-photo--${isLegacy ? 'landscape' : orientation}`)}>
                                <img className="rev-wm-photo__img" src={sample.src} alt="" />
                                {hasLogo && settings.enabled && logoUrl &&
                                    <img className="rev-wm-photo__logo" src={logoUrl} style={logoStyle} alt={t('eventWatermark.preview.alt')} /> /* inline-ok: geometría en % calculada por previewStyle (posición, tamaño y opacidad del logo), no hay clase posible */}
                                {isLegacy &&
                                    <img className="rev-wm-photo__overlay" src={legacyUrl} alt={t('eventWatermark.preview.alt')} />}
                                {isDefault && !loading && settings.enabled &&
                                    <img className="rev-wm-photo__overlay" src={DEFAULT_OVERLAY[orientation]} alt={t('eventWatermark.preview.alt')} />}
                            </div>
                            <span className="rev-mono rev-text-muted rev-wm-stage__summary">{summary}</span>
                        </div>
                        {!loading &&
                            <div
                                className={cx('rev-wm-change', dragOver && 'is-dragover')}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={onDrop}>
                                <RevButton variant="secondary" size="sm" onClick={openPicker} disabled={saving}>
                                    {hasLogo ? t('eventWatermark.preview.change') : t('eventWatermark.preview.drop')}
                                </RevButton>
                                <span className="rev-help">{t('eventWatermark.preview.dropHint')}</span>
                                {!isLegacy &&
                                    <RevSegmented className="rev-wm-change__orientation" items={orientationItems} value={orientation} onChange={setOrientation} aria-label={t('eventWatermark.preview.orientation.label')} />}
                            </div>}
                        <input ref={fileInputRef} type="file" accept="image/png,.png" hidden onChange={onInputChange} />
                    </RevCard>
                    <RevCard title={t('eventWatermark.placement.title')}>
                        <RevField label={t('eventWatermark.placement.position')}>
                            <RevSegmented items={posItems} value={settings.position} onChange={(v) => update({ position: v })} disabled={!hasLogo || saving} />
                        </RevField>
                        <RevField label={t('eventWatermark.placement.size')}>
                            <RevSegmented items={sizeItems} value={settings.size} onChange={(v) => update({ size: v })} disabled={!hasLogo || saving} />
                        </RevField>
                        <RevField label={t('eventWatermark.placement.opacity')} help={t('eventWatermark.placement.opacityHelp')}>
                            <RevSegmented items={opacityItems} value={settings.opacity} onChange={(v) => update({ opacity: v })} disabled={!hasLogo || saving} />
                        </RevField>
                    </RevCard>
                </div>
                <div className="rev-cols__aside">
                    <RevCard title={t('eventWatermark.rules.title')}>
                        <label className="rev-switch-field">
                            <input
                                type="checkbox"
                                className="rev-switch-input"
                                checked={settings.enabled}
                                disabled={isLegacy || loading || saving}
                                onChange={(e) => update({ enabled: e.target.checked })} />
                            <RevSwitch on={settings.enabled} />
                            <span>{t('eventWatermark.rules.switch')}</span>
                        </label>
                        <RevKvRow
                            ruled
                            tone={galleryValue.tone}
                            k={t('eventWatermark.rules.gallery.k')}
                            v={galleryValue.v} />
                        <RevKvRow
                            ruled
                            tone="ok"
                            k={t('eventWatermark.rules.purchased.k')}
                            v={t('eventWatermark.rules.purchased.v')} />
                        <RevKvRow
                            ruled
                            tone="ok"
                            k={t('eventWatermark.rules.panel.k')}
                            v={t('eventWatermark.rules.panel.v')} />
                        <span className="rev-help">{t('eventWatermark.rules.note')}</span>
                        {isDefault && settings.enabled && <span className="rev-help">{t('eventWatermark.rules.none')}</span>}
                    </RevCard>
                    <RevCard title={t('eventWatermark.file.title')}>
                        <RevTipRow n="01" title={t('eventWatermark.file.s1')} />
                        <RevTipRow n="02" title={t('eventWatermark.file.s2', { size: SIZES[settings.size], min: minLogoWidth(settings.size) })} />
                        <RevTipRow n="03" title={t('eventWatermark.file.s3')} />
                    </RevCard>
                </div>
            </div>
            <RevBarra sangre ok={barra.ok} error={barra.error}>
                <RevButton variant="secondary" onClick={dispatchCancel} disabled={!dirty || saving}>{t('generic.cancelButton.text')}</RevButton>
                <RevButton variant="primary" onClick={dispatchSubmit} disabled={!canSave} loading={saving}>
                    {saving ? t('eventWatermark.actions.saving') : t('eventWatermark.actions.save')}
                </RevButton>
            </RevBarra>
            {errorMessage !== undefined &&
                <Modal
                    title={t('generic.errorAlert.title')}
                    message={errorMessage}
                    showConfirmButton={false}
                    showCancelButton={true}
                    cancelButtonText={t('generic.alertButton.close')}
                    onCancel={() => setErrorMessage(undefined)}
                />}
        </>
    );
};

export default EventWatermark;
